import { useMemo } from 'react';

import { REFERENCE_STALE_TIME, useApiQuery } from '@/hooks/api';
import { queryKeys } from '@/shared/lib/queryKeys';

type UseStableFacetCountsOptions<TCounts> = {
  listingId: string;
  currentCounts: TCounts | undefined;
  isFiltersActive: boolean;
  fetchBaselineCounts: () => Promise<TCounts | undefined>;
  mergeCounts: (baseline: TCounts, current: TCounts | undefined) => TCounts;
  enabled?: boolean;
  context?: unknown;
};

/**
 * Keeps a baseline facet catalog (unfiltered fetch + snapshots when filters are cleared)
 * and merges current omit-facet counts onto it so sidebar option lists stay stable.
 */
export function useStableFacetCounts<TCounts>({
  listingId,
  currentCounts,
  isFiltersActive,
  fetchBaselineCounts,
  mergeCounts,
  enabled = true,
  context,
}: UseStableFacetCountsOptions<TCounts>): TCounts | undefined {
  const { data: baseline } = useApiQuery({
    queryKey: queryKeys.listings.facetBaseline(listingId, context),
    queryFn: fetchBaselineCounts,
    enabled,
    staleTime: REFERENCE_STALE_TIME,
  });

  const snapshotBaseline = !isFiltersActive ? currentCounts : undefined;
  const effectiveBaseline = snapshotBaseline ?? baseline;

  return useMemo(() => {
    if (!effectiveBaseline) return currentCounts;
    return mergeCounts(effectiveBaseline, currentCounts);
  }, [effectiveBaseline, currentCounts, mergeCounts]);
}
